/**
 * Mobile sidebar drawer (off-canvas) for the office shell.
 *
 * Desktop (lg+) is untouched: the <aside id="sidebar"> is pinned open by
 * `lg:translate-x-0` and the hamburger button is `lg:hidden`. On mobile the
 * aside starts off-canvas (`-translate-x-full`); this module slides it in/out.
 *
 * Why not Flowbite's Drawer: the sidebar is OOB-swapped by HTMX on every
 * boosted navigation (`hx-select-oob="#sidebar"`), so a Drawer instance
 * created at init time keeps pointing at the detached old node — clicking
 * the hamburger then toggles classes on a dead element and nothing visible
 * happens. Everything here is event-delegated on `document` and resolves
 * `#sidebar` at click time, so swaps can never stale-reference.
 *
 * Wiring (all optional-presence, all delegated):
 *   - [data-mobile-sidebar-toggle]  → hamburger in the topbar
 *   - [data-mobile-sidebar-close]   → X button inside the sidebar (lg:hidden)
 *   - backdrop click / Escape       → close
 *   - boosted nav (sidebar link)    → the OOB swap re-inserts the aside in
 *     its closed markup state; the afterSwap hook below just clears the
 *     backdrop + scroll lock.
 */

const BACKDROP_ID = 'mobile-sidebar-backdrop';

function sidebarEl() {
  return document.getElementById('sidebar');
}

function isOpen() {
  const el = sidebarEl();
  return !!el && !el.classList.contains('-translate-x-full');
}

function setHamburgerExpanded(expanded) {
  document.querySelectorAll('[data-mobile-sidebar-toggle]').forEach((btn) => {
    btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
  });
}

function removeBackdrop() {
  const backdrop = document.getElementById(BACKDROP_ID);
  if (backdrop) backdrop.remove();
  document.body.classList.remove('overflow-hidden');
}

function openSidebar() {
  const el = sidebarEl();
  if (!el) return;
  el.classList.remove('-translate-x-full');
  el.removeAttribute('aria-hidden');
  if (!document.getElementById(BACKDROP_ID)) {
    const backdrop = document.createElement('div');
    backdrop.id = BACKDROP_ID;
    // z-30 sits under the aside (z-40) but over the page + topbar content.
    backdrop.className = 'fixed inset-0 z-30 bg-gray-900/50 dark:bg-gray-900/80 lg:hidden';
    document.body.appendChild(backdrop);
  }
  document.body.classList.add('overflow-hidden');
  setHamburgerExpanded(true);
}

function closeSidebar() {
  const el = sidebarEl();
  if (el) el.classList.add('-translate-x-full');
  removeBackdrop();
  setHamburgerExpanded(false);
}

// Flowbite used to set aria-hidden="true" on the sidebar even on desktop
// where it's always visible, tripping a11y checks on focusable children.
// Keep the desktop guard.
function fixSidebarAriaHidden() {
  const el = sidebarEl();
  if (el && window.matchMedia('(min-width: 1024px)').matches) {
    el.removeAttribute('aria-hidden');
  }
}

document.addEventListener('click', (evt) => {
  const target = evt.target;
  if (!target || !target.closest) return;

  if (target.closest('[data-mobile-sidebar-toggle]')) {
    evt.preventDefault();
    if (isOpen()) closeSidebar();
    else openSidebar();
    return;
  }

  if (target.closest('[data-mobile-sidebar-close]')) {
    evt.preventDefault();
    closeSidebar();
    return;
  }

  if (target.id === BACKDROP_ID) {
    closeSidebar();
    return;
  }

  // A sidebar link tapped on mobile: start closing right away so the swap
  // feels snappy (the OOB-swapped aside comes back closed anyway — this
  // mostly clears the backdrop without waiting for the response).
  if (
    document.getElementById(BACKDROP_ID) &&
    target.closest('#sidebar a')
  ) {
    closeSidebar();
  }
});

document.addEventListener('keydown', (evt) => {
  if (evt.key === 'Escape' && isOpen() && document.getElementById(BACKDROP_ID)) {
    closeSidebar();
  }
});

// The OOB swap replaces the aside with closed markup; make sure no orphan
// backdrop / scroll lock survives the navigation or a history restore.
document.body.addEventListener('htmx:afterSwap', removeBackdrop);
document.body.addEventListener('htmx:historyRestore', removeBackdrop);

window.addEventListener('load', () => {
  fixSidebarAriaHidden();
  window.addEventListener('resize', () => {
    fixSidebarAriaHidden();
    // Crossing into desktop with the drawer open would leave a stale
    // backdrop behind the always-visible sidebar.
    if (window.matchMedia('(min-width: 1024px)').matches) removeBackdrop();
  });
});

window.reinitSidebar = fixSidebarAriaHidden;
